/** @format */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TooltipContainer, TooltipContent, PortaledTooltipContent } from './PriceTooltip.style';
import { Icon } from '@/components/ui/Icon';
import ToolTipIcon from '@/assets/icons/tooltip-icon.svg';
import useWindowSize from '@/hooks/useWindowSize';

type PriceTooltip = {
  formattedDiscountUntilDate: string | boolean;
};

export const PriceTooltip: React.FC<PriceTooltip> = ({ formattedDiscountUntilDate }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  const isMobile = width ? width < 500 : false;

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      if (isMobile) {
        const leftPos = Math.min(rect.left + rect.width / 2, window.innerWidth * 0.85);

        setPosition({
          top: rect.top - 45,
          left: leftPos,
        });
      } else {
        setPosition({
          top: rect.top - 45,
          left: rect.left + rect.width / 2,
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      const handleOutsideClick = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('click', handleOutsideClick);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
        document.removeEventListener('click', handleOutsideClick);
      };
    }
  }, [isOpen, isMobile]);

  const handleMouseEnter = () => {
    if (window.matchMedia('(pointer: fine)').matches) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.matchMedia('(pointer: fine)').matches) {
      setIsOpen(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <TooltipContainer
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => {
          e.stopPropagation();
        }}
      >
        <Icon color="#015962" icon={ToolTipIcon} width={16} height={16} />
        <TooltipContent>{formattedDiscountUntilDate}</TooltipContent>
      </TooltipContainer>

      {isOpen &&
        createPortal(
          <PortaledTooltipContent
            style={{
              top: `${position.top - (isMobile ? 20 : 0)}px`,
              left: `${position.left}px`,
              transform: 'translateX(-50%)',
            }}
          >
            {formattedDiscountUntilDate}
          </PortaledTooltipContent>,
          document.body,
        )}
    </>
  );
};
